import { UrlEventType, UrlParameter, VisitType } from 'src/requester';
import {
  ConversionDataType,
  ConversionParametersType,
  CustomDataType,
  IKameleoonData,
  KameleoonData,
} from './types';
import { TrackingStatus } from 'src/types';
import { CustomData } from 'src/kameleoonData/customData';

/**
 * @class
 * Conversion - a class for creating an instance for conversion tracking
 * */
export class Conversion implements IKameleoonData {
  public status: TrackingStatus;

  private goalId: number;
  private revenue: number;
  private negative: boolean;
  private id: number;
  private nonce: string | undefined;
  private metadata: CustomData[] | undefined;

  /**
   * @param {ConversionParametersType} conversionParameters - an object with conversion parameters
   * */
  constructor({
    goalId,
    revenue = 0,
    negative = false,
    metadata,
  }: ConversionParametersType) {
    this.goalId = goalId;
    this.revenue = revenue;
    this.negative = negative;
    this.status = TrackingStatus.Unsent;
    this.id = Math.floor(Math.random() * 1_000_000);
    this._metadata = metadata;
  }

  set _id(id: number) {
    this.id = id;
  }

  set _nonce(nonce: string) {
    this.nonce = nonce;
  }

  get url(): string {
    return (
      UrlEventType.Conversion +
      UrlParameter.GoalId +
      String(this.goalId) +
      UrlParameter.Revenue +
      String(this.revenue) +
      UrlParameter.Negative +
      String(this.negative) +
      (this.metadata?.length
        ? UrlParameter.Metadata + this._encodeMetadata()
        : '')
    );
  }

  /**
   * @private
   * @method metadata - an internal getter for a metadata of conversion
   * */
  get _metadata(): CustomData[] | undefined {
    return this.metadata;
  }

  /**
   * @private
   * @method metadata - an internal setter for setting metadata of conversion
   * @param {number} value - an index value
   * */
  public set _metadata(value: CustomData[] | undefined) {
    this.metadata = value;
  }

  get data(): ConversionDataType {
    return {
      goalId: this.goalId,
      nonce: this.nonce,
      revenue: this.revenue,
      negative: this.negative,
      type: KameleoonData.Conversion,
      status: this.status,
      id: this.id,
      metadata:
        this.metadata?.map((item: CustomData) => item.data) || undefined,
    };
  }

  private _encodeMetadata(): string {
    if (!this.metadata?.length) {
      return '';
    }
    const indices: Set<number> = new Set();
    return encodeURIComponent(
      JSON.stringify(
        this.metadata.reduce((acc: { [key: number]: string[] }, item) => {
          if (indices.has(item.data.index)) {
            return acc;
          }
          indices.add(item.data.index);
          acc[item.data.index] = item.data.value;
          return acc;
        }, {}),
      ),
    );
  }

  /**
   * @private
   * @method _fromRaw - an internal method for creating a Conversion instance from raw data
   * @param {ConversionDataType} data - a raw data
   * @return {Conversion} a Conversion instance
   * */
  public static _fromRaw(data: ConversionDataType): Conversion {
    const { goalId, revenue, negative, status, id, nonce, metadata } = data;

    const conversion = new Conversion({
      goalId,
      revenue,
      negative,
      metadata: Conversion.parseMetadata(metadata),
    });

    conversion._id = id;
    conversion.status = status;

    if (nonce) {
      conversion._nonce = nonce;
    }

    return conversion;
  }

  private static parseMetadata(
    metadata: CustomDataType[] | undefined,
  ): CustomData[] | undefined {
    return (
      metadata?.map((item: CustomDataType) => CustomData._fromRaw(item)) ||
      undefined
    );
  }

  /**
   * @private
   * @method _listFromVisit - an internal method for returning a list of all Conversion instances
   * @param {VisitType} visit - a visits
   * @return {Conversion[]} - a list of Conversion instances
   * */
  public static _listFromVisit(visit: VisitType): Conversion[] {
    const conversions: Conversion[] = [];

    if (!visit.conversionEvents?.length) {
      return [];
    }

    if (visit.conversionEvents?.length) {
      for (const conversionEvent of visit.conversionEvents) {
        const { goalId, revenue, negative } = conversionEvent.data;
        const conversion = new Conversion({ goalId, revenue, negative });

        // --- Note ---
        // Conversion is marked as sent, so the visitor won't be converted again just
        // by pulling visits data
        conversion.status = TrackingStatus.Sent;
        conversions.push(conversion);
      }
    }

    return conversions;
  }

  /**
   * @private
   * @method _updateFromVisit - an internal method for updating a list of Conversion instances with only goalId
   * @param {VisitType} visit - a visits
   * @param {Map<number, Conversion>} dataMap - a map of Conversion instances
   * */
  public static _updateFromVisit(
    visit: VisitType,
    dataMap: Map<number, Conversion>,
  ): void {
    if (visit.conversionEvents?.length) {
      for (const conversionEvent of visit.conversionEvents) {
        const { goalId } = conversionEvent.data;

        // --- Note ---
        // - We only take unique `goalId`s as and don't use `revenue` and `negative`
        // because we only utilize it for Conversion Condition and don't re-sent this data tracking
        // not to break server `revenue` calculations and not to cause extra conversions
        // - Moreover conversion is marked as sent, so the visitor won't be converted again just
        // by pulling visits data
        if (!dataMap.has(goalId)) {
          const conversion = new Conversion({ goalId });

          conversion.status = TrackingStatus.Sent;
          dataMap.set(goalId, conversion);
        }
      }
    }
  }
}
